import React, {useEffect, useRef} from 'react';
import ReactDOM from 'react-dom/client';

import notifications from 'notifications';
import * as ReduxDOMRender from 'redux-stores/ReduxDOMRender';

const ADBLOCK_WARNING_SHOWN_KEY = 'adblockWarningShown';

/** @package */
export function AdblockWarning({detectionDelayMs = 200}: {detectionDelayMs?: number}): JSX.Element {
  const adBoxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Don't pop up if we've already shown the warning
    const hasShownWarning = sessionStorage.getItem(ADBLOCK_WARNING_SHOWN_KEY) === 'true';
    if (hasShownWarning) {
      return;
    }

    const timeoutId = setTimeout(() => {
      if (adBoxRef.current) {
        const hasAdBlock = window.getComputedStyle(adBoxRef.current)?.display === 'none';

        if (hasAdBlock) {
          sessionStorage.setItem(ADBLOCK_WARNING_SHOWN_KEY, 'true');

          notifications.warn(
            'Some functionality may be affected by your ad blocker — please disable it or add Benchling to your allowlist to resolve this issue.',
            {
              closeable: true,
              duration: 0,
            }
          );
        }
      }
    }, detectionDelayMs);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [detectionDelayMs]);

  return (
    <div ref={adBoxRef} className="ad-box" style={{position: 'fixed', top: 0, left: 0}} aria-hidden="true">
      &nbsp;
    </div>
  );
}

/**
 * Mount a React component to detect the presence of an ad blocker. If an ad blocker is detected, it will throw a warning toast.
 *
 * This is necessary because ad blockers can interfere with CMD-C copy paste, mostly for entity chips.
 */
export function mountAdblockWarning(root: ReactDOM.Root | null): ReactDOM.Root {
  const node = document.createElement('div');
  document.body.appendChild(node);
  return ReduxDOMRender.renderToDOM(<AdblockWarning />, node, root);
}
